// Per-family activation tracker (Module 36).
//
// Aggregates per-parent activation signals so admins can spot which
// families are warmed up vs. still need a personal nudge on launch day.
//
// Signals (no new tables — all admin-RLS readable already):
//   * push_enabled    — at least one push_subscriptions row for the profile
//   * has_messaged    — at least one messages row with sender_id = profile
//
// We intentionally do NOT track "PWA installed" server-side: there is no
// reliable signal in PostgREST land. The closest proxy is push_enabled
// (since enabling push usually happens after install on iOS/Android).
//
// Returns a flat array, one row per parent member of the family, in the
// same order the family.members were passed in.

import { supabase, isSupabaseConfigured } from "./supabase";

export async function loadFamilyActivation(profileIds) {
  if (!isSupabaseConfigured) return new Map();
  if (!Array.isArray(profileIds) || profileIds.length === 0) return new Map();

  const [pushRes, msgRes] = await Promise.all([
    supabase
      .from("push_subscriptions")
      .select("profile_id")
      .in("profile_id", profileIds),
    supabase
      .from("messages")
      .select("sender_id")
      .in("sender_id", profileIds),
  ]);

  const pushSet = new Set((pushRes.data || []).map((r) => r.profile_id));
  const msgSet  = new Set((msgRes.data || []).map((r) => r.sender_id));

  const out = new Map();
  for (const id of profileIds) {
    out.set(id, {
      push_enabled: pushSet.has(id),
      has_messaged: msgSet.has(id),
    });
  }
  return out;
}

/**
 * Single-letter activation score for a parent. Used by the
 * activation chip in the family sheet header.
 *   warm  — both signals on
 *   half  — exactly one signal on
 *   cool  — neither signal on (most-actionable nudge candidate)
 */
export function activationLevel(activation) {
  if (!activation) return "cool";
  const score = (activation.push_enabled ? 1 : 0) + (activation.has_messaged ? 1 : 0);
  if (score >= 2) return "warm";
  if (score === 1) return "half";
  return "cool";
}

export const ACTIVATION_TONE = {
  warm: { label: "Warm",       pill: "border-primary/30 bg-primary/10 text-primary-hover" },
  half: { label: "Halfway",    pill: "border-[#EFBA57]/40 bg-[#EFBA57]/10 text-[#8A5A12]" },
  cool: { label: "Needs nudge",pill: "border-neutral-200 bg-neutral-50 text-neutral-600" },
};
